import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from '../../axiosConfig';
import '../../styles/assetShareLinks.css';

export default function ShareUnlock() {
  const { token } = useParams();
  const [meta, setMeta] = useState(null);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loadingMeta, setLoadingMeta] = useState(true);
  const [unlocking, setUnlocking] = useState(false);
  const [session, setSession] = useState(null);
  const [viewerUrl, setViewerUrl] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoadingMeta(true);
      setError('');
      try {
        const { data } = await axios.get(`/share/public/${encodeURIComponent(token)}`);
        if (!cancelled) setMeta(data);
      } catch (err) {
        if (!cancelled) {
          setMeta(null);
          setError(err.response?.data?.error || 'Share link not found');
        }
      } finally {
        if (!cancelled) setLoadingMeta(false);
      }
    };
    if (token) load();
    return () => {
      cancelled = true;
    };
  }, [token]);

  useEffect(() => {
    return () => {
      if (viewerUrl) URL.revokeObjectURL(viewerUrl);
    };
  }, [viewerUrl]);

  const openFile = async (mode, accessToken) => {
    const access = accessToken || session?.access_token;
    if (!token || !access) return;
    setError('');
    try {
      const res = await axios.get(`/share/public/${encodeURIComponent(token)}/file`, {
        params: { access, mode },
        responseType: 'blob',
      });
      const blob = new Blob([res.data], {
        type: res.headers['content-type'] || 'application/pdf',
      });
      const url = URL.createObjectURL(blob);
      if (mode === 'download') {
        const ctype = String(res.headers['content-type'] || '');
        let ext = '.pdf';
        if (ctype.includes('zip')) ext = '.zip';
        else if (ctype.includes('png')) ext = '.png';
        else if (ctype.includes('jpeg') || ctype.includes('jpg')) ext = '.jpg';
        const a = document.createElement('a');
        a.href = url;
        a.download = `${(meta?.asset_label || 'file').replace(/\s+/g, '_')}${ext}`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 1500);
      } else {
        setViewerUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return url;
        });
      }
    } catch (err) {
      let message = 'Could not open the file';
      const data = err.response?.data;
      if (data instanceof Blob) {
        try {
          const text = await data.text();
          const parsed = JSON.parse(text);
          message = parsed.error || message;
        } catch {
          /* ignore */
        }
      } else if (err.response?.data?.error) {
        message = err.response.data.error;
      }
      setError(message);
      if (err.response?.status === 401) {
        setSession(null);
        setViewerUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return null;
        });
      }
    }
  };

  const handleUnlock = async (e) => {
    e.preventDefault();
    setUnlocking(true);
    setError('');
    try {
      const { data } = await axios.post(`/share/public/${encodeURIComponent(token)}/unlock`, {
        password,
      });
      setSession(data);
      setPassword('');
      if (data.can_view) {
        await openFile('view', data.access_token);
      }
    } catch (err) {
      setSession(null);
      setError(err.response?.data?.error || 'Incorrect access key');
    } finally {
      setUnlocking(false);
    }
  };

  if (loadingMeta) {
    return (
      <div className="share-unlock-page">
        <div className="share-unlock-card">
          <p className="share-unlock-asset">Loading share link…</p>
        </div>
      </div>
    );
  }

  if (!meta) {
    return (
      <div className="share-unlock-page">
        <div className="share-unlock-card">
          <p className="share-unlock-eyebrow">Protected link</p>
          <h1>Link unavailable</h1>
          <div className="share-unlock-error">{error || 'This share link was not found.'}</div>
        </div>
      </div>
    );
  }

  if (!meta.usable) {
    return (
      <div className="share-unlock-page">
        <div className="share-unlock-card">
          <p className="share-unlock-eyebrow">Protected link</p>
          <h1>{meta.book_title}</h1>
          <p className="share-unlock-asset">
            Asset: <strong>{meta.asset_label}</strong>
          </p>
          <div className="share-unlock-error">
            {meta.message || 'This share link is no longer available.'}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`share-unlock-page${session ? ' is-unlocked' : ''}`}>
      <div className={`share-unlock-card${session ? ' is-unlocked' : ''}`}>
        <div className="share-unlock-header">
          <div className="share-unlock-header-text">
            <p className="share-unlock-eyebrow">Protected link · one file only</p>
            <h1>{meta.book_title}</h1>
            <p className="share-unlock-asset">
              This link unlocks <strong>{meta.asset_label}</strong> only — not other formats.
            </p>
          </div>
          {session?.can_download ? (
            <div className="share-unlock-actions share-unlock-actions-header">
              <button type="button" className="primary" onClick={() => openFile('download')}>
                Download {meta.asset_label}
              </button>
            </div>
          ) : null}
        </div>

        {error ? <div className="share-unlock-error">{error}</div> : null}

        {!session ? (
          <form className="share-unlock-form" onSubmit={handleUnlock}>
            <label htmlFor="share-password">Access key</label>
            <input
              id="share-password"
              type="password"
              autoComplete="off"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter the password you were given"
              required
            />
            <button type="submit" disabled={unlocking || !password.trim()}>
              {unlocking ? 'Checking…' : 'Unlock'}
            </button>
          </form>
        ) : (
          <>
            <div className="share-unlock-viewer">
              {viewerUrl ? (
                <iframe title={meta.asset_label} src={viewerUrl} />
              ) : (
                <div className="share-unlock-viewer-empty">Preparing file…</div>
              )}
            </div>
            <p className="share-unlock-note">
              Access lasts about 15 minutes. After that, enter the password again.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
